/* eslint-disable react/no-array-index-key */
// dependencies
import React from "react";

import DatePicker from "../../../../components/Inputs/DatePicker";
// components
import SelectInput from "../../../../components/Inputs/SelectInput";
// components
import TextInput from "../../../../components/Inputs/TextInput";
// config
import Config from "./Config";
import CustomFieldPicker from "./CustomFieldPicker";
import FieldValuesPicker from "./FieldValuesPicker";
import { GoalCategoryPicker, GoalOutlookPicker, GoalPicker } from "./GoalPickers";
import GroupPicker from "./GroupPicker";
import PeoplePicker from "./PeoplePicker";
import PersonPicker from "./PersonPicker";
import QueryButton from "./QueryButton";
import ReviewCyclePicker from "./ReviewCyclePicker";
import ReviewFieldPicker from "./ReviewFieldPicker";
import ReviewTemplatePicker from "./ReviewTemplatePicker";
import StatusSelector from "./StatusSelector";

// schema
import { FieldFilter, InputValue } from "../../schemas/report";

import { InputType, handleSetInput } from "./utils";

// utils
// styles
import css from "./QueryBuilder.module.scss";

interface BaseProps {
	filterBuilder: FieldFilter[];
	updateFilterBuilder: (filterBuilder: FieldFilter[]) => void;
	handleClear: (filterIndex: number) => void;
}

const Form = (props: BaseProps) => {
	const { filterBuilder, updateFilterBuilder, handleClear } = props;
	const category = filterBuilder[0]?.category;

	const handleInputChange = (val: InputValue, filterItemIndex: number, inputType: InputType) => {
		const newFilterBuilder = handleSetInput(inputType, val, [...filterBuilder], filterItemIndex);
		updateFilterBuilder(newFilterBuilder);
	};

	return (
		<div>
			{filterBuilder.map((filterItem, filterItemIndex) => {
				let comparisonValue = "";
				if (filterItem.type && !filterItem.comparison) {
					[comparisonValue] = Config.fieldTypeOptionsLookup[filterItem.type];
				} else {
					comparisonValue = filterItem.comparison;
				}

				return (
					<div key={filterItemIndex}>
						{[
							"REVIEW_CYCLE_FIELD",
							"EMPLOYEE_FIELD",
							"REVIEW_FORM_FIELD",
							"REVIEW_FIELD",
							"GOAL_FIELD",
							"SURVEY_FIELD",
						].includes(category) ? (
							<>
								{filterItemIndex > 0 ? (
									<div className={css.conditionTitle}>
										{filterItem.field ? "Edit Condition" : "Add Condition"}
										<div
											data-testid="clear-or"
											onClick={() => handleClear(filterItemIndex)}
											className={css.clearButton}
										>
											Clear
										</div>
									</div>
								) : null}
								{category === "EMPLOYEE_FIELD" ? (
									<div data-testid="builder-field" className={css.conditionInput}>
										<CustomFieldPicker
											onChange={(val) => handleInputChange(val, filterItemIndex, "field")}
											value={filterItem.field ? filterItem.field : null}
											isDisabled={filterItemIndex > 0}
										/>
									</div>
								) : (
									<div data-testid="builder-field" className={css.conditionInput}>
										<ReviewFieldPicker
											fieldCategory={category}
											onChange={(value) => handleInputChange(value, filterItemIndex, "field")}
											value={filterItem.field || null}
											isDisabled={filterItemIndex > 0}
										/>
									</div>
								)}
								{filterItem.field ? (
									<div data-testid="builder-comparison" className={css.conditionInput}>
										<SelectInput
											results={Config.fieldTypeOptionsLookup[filterItem.type]}
											name="comparison"
											onChange={(value) =>
												handleInputChange(value, filterItemIndex, "comparison")
											}
											value={comparisonValue}
										/>
									</div>
								) : null}
								{["text", "textbox"].includes(filterItem.type) ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<TextInput
											placeholder="Input text here"
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value}
										/>
									</div>
								) : null}
								{filterItem.type === "phone" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<TextInput
											type="tel"
											placeholder="Input phone number"
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value}
										/>
									</div>
								) : null}
								{filterItem.type === "email" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<TextInput
											type="email"
											placeholder="Input email address"
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value}
										/>
									</div>
								) : null}
								{filterItem.type === "date" ? (
									<div>
										<div data-testid="builder-date" className={css.conditionInput}>
											<DatePicker
												hasIcon
												onChange={(value: Date | null) =>
													handleInputChange(value, filterItemIndex, "value")
												}
												value={
													filterItem.value === "" ? "" : new Date(filterItem.value as string)
												}
											/>
										</div>
									</div>
								) : null}
								{["person", "manager"].includes(filterItem.type) ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<PersonPicker
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value ? filterItem.value : {}}
										/>
									</div>
								) : null}
								{["numeric", "monetary", "salary"].includes(filterItem.type) ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<TextInput
											placeholder="Input number here"
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value}
											type="number"
										/>
									</div>
								) : null}
								{filterItem.type === "multi-select" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<FieldValuesPicker
											isMultiSelect={true}
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value ? (filterItem.value as string[]) : []}
											customFieldName={filterItem.field.toString()}
										/>
									</div>
								) : null}
								{filterItem.type === "single-select" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<FieldValuesPicker
											isMultiSelect={false}
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value ? ([filterItem.value] as string[]) : []}
											customFieldName={filterItem.field.toString()}
										/>
									</div>
								) : null}
								{filterItem.type === "department" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<GroupPicker
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value ? filterItem.value : null}
										/>
									</div>
								) : null}
								{filterItem.type === "template" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<ReviewTemplatePicker
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value ? filterItem.value : {}}
										/>
									</div>
								) : null}
								{filterItem.type === "cycle" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<ReviewCyclePicker
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value !== "" ? filterItem.value : {}}
										/>
									</div>
								) : null}
								{filterItem.type === "people" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<PeoplePicker
											value={filterItem.value ? (filterItem.value as string[]) : []}
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
										/>
									</div>
								) : null}
								{filterItem.type === "boolean" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<SelectInput
											setFirstResult={false}
											results={[
												{ value: "true", label: "Yes" },
												{ value: "false", label: "No" },
											]}
											name="booleanValue"
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value?.toString()}
										/>
									</div>
								) : null}
								{filterItem.type === "status" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<StatusSelector
											fieldCategory={category}
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value}
										/>
									</div>
								) : null}
								{/* Goal Specific Fields */}
								{filterItem.type === "goal" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<GoalPicker
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value as string}
										/>
									</div>
								) : null}
								{filterItem.type === "goalCategories" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<GoalCategoryPicker
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value ? filterItem.value : null}
										/>
									</div>
								) : null}
								{filterItem.type === "goalOutlook" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<GoalOutlookPicker
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value as string}
										/>
									</div>
								) : null}
								{filterItem.type === "goalTransparency" ? (
									<div data-testid="builder-value" className={css.conditionInput}>
										<SelectInput
											results={[
												{ value: "private", label: "Private" },
												{ value: "public", label: "Public" },
											]}
											name="goalTransparency"
											onChange={(value) => handleInputChange(value, filterItemIndex, "value")}
											value={filterItem.value?.toString()}
										/>
									</div>
								) : null}
								{filterItemIndex < filterBuilder.length - 1 ? (
									<div className={css.conditionConnector}>
										<QueryButton text="OR" isStatic={true} />
									</div>
								) : null}
							</>
						) : null}
					</div>
				);
			})}
		</div>
	);
};

export default Form;
